import { KeyboardEvent, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Button, ButtonType } from '../../../components';

// Mirrors the search-by-name dropdown in sitters-search-filter-body.tpl.html.
// URL param: `name` (free-text caregiver name). Staged locally and committed
// on Search (or Enter) so keystroke-per-character searches don't thrash the
// sitter results query.
export const NameSearchFilter = (): JSX.Element => {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlValue = searchParams.get('name') ?? '';
  const [draft, setDraft] = useState(urlValue);

  useEffect(() => {
    setDraft(urlValue);
  }, [urlValue]);

  const apply = (value: string): void => {
    setSearchParams(
      (sp) => {
        const next = new URLSearchParams(sp);
        const trimmed = value.trim();
        if (trimmed) next.set('name', trimmed);
        else next.delete('name');
        next.set('page', '1');
        return next;
      },
      { replace: true },
    );
  };

  const onKeyUp = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') apply(draft);
  };

  const clear = (): void => {
    setDraft('');
    apply('');
  };

  return (
    <section className='mb-4'>
      <h4 className='font-semibold mb-1'>Name</h4>
      {urlValue && (
        <div className='flex items-center justify-between border border-neutral-200 rounded px-2 py-1 text-sm mb-2'>
          <span>{urlValue}</span>
          <Button
            buttonType={ButtonType.tertiary}
            id='clear-name'
            aria-label='Clear name filter'
            onClick={clear}
            className='text-neutral-500 hover:text-neutral-700 px-1'
          >
            &times;
          </Button>
        </div>
      )}
      <input
        type='text'
        id='search-name-text'
        aria-label='Caregiver name'
        placeholder='Search caregivers by name'
        className='form-control w-full border border-neutral-300 rounded px-2 py-1 text-sm mb-2'
        value={draft}
        onChange={(e): void => setDraft(e.target.value)}
        onKeyUp={onKeyUp}
      />
      <Button
        buttonType={ButtonType.primary}
        id='search-name'
        onClick={(): void => apply(draft)}
        className='btn btn-primary w-full'
      >
        Search
      </Button>
    </section>
  );
};
